import { useState, useMemo } from "react";
import { VStack, HStack } from "../../../../../styled-system/jsx";
import { useAppSelector } from "../../../../redux/hooks";
import { differenceInDays, subDays, format } from "date-fns";
import getMetricSpec, { getFormatter, metricNameSelector } from "../../../merchandising/utils/metrics";
import { useTranslation } from "react-i18next";
import { MARKETPLACE_TIMEZONE } from "../../../../utils/marketplaceConstants";
import { Select } from "../../../../components/fields/base/Select";
import { Switch } from "../../../../components/fields/base/Switch";
import { css } from "../../../../../styled-system/css";
import { HourChart } from "./HourChart";
import { DaypartingGrid } from "./DaypartingGrid";
import { DaypartingControls } from "./DaypartingControls";
import { useDaypartingSelection } from "../hooks/useDaypartingSelection";
import { useDaypartingColors } from "../hooks/useDaypartingColors";
import { useDaypartingValues } from "../hooks/useDaypartingValues";
import { useCampaignStatsHourly } from "../api/useCampaignStatsHourly";
import type { DaypartingData } from "../types";
import { createCellKey, parseCellKey } from "../utils";

interface DayPartingFormProps {
  advertisements: number[];
  categoryId?: number;
  initialValue: DaypartingData;
  onChange?: (data: DaypartingData) => void;
  size?: "lg" | "sm";
  canUpdate?: boolean;
  startDate: Date;
  endDate: Date;
}

const containerStyles = css({
  fontSize: "medium",
});

const periodInfoStyles = css({
  fontSize: "medium",
  textAlign: "right",
});

const errorTextStyles = css({
  color: "error",
});

export function DayPartingForm({
  advertisements,
  categoryId,
  initialValue,
  onChange,
  size = "lg",
  canUpdate = true,
  startDate: _startDate,
  endDate: _endDate,
}: DayPartingFormProps) {
  const [start, end, dateError] = useMemo((): [Date, Date, boolean] => {
    const now = new Date();

    const nowDiff = differenceInDays(now, _startDate);
    if (nowDiff > 30) {
      return [subDays(now, 30), now, true];
    }

    const diff = differenceInDays(_endDate, _startDate);
    if (diff > 30) {
      return [subDays(_endDate, 30), _endDate, true];
    }
    return [_startDate, _endDate, false];
  }, [_startDate, _endDate]);

  const marketPlace = useAppSelector((state) => state.marketPlace);

  const [isFilterAdvertisement, setIsFilterAdvertisement] = useState(true);
  const filters = useMemo(() => {
    let filterStr = `advertisement__in:${advertisements.join("|")}`;
    if (!isFilterAdvertisement && categoryId !== undefined) {
      filterStr = `category_id:${categoryId}`;
    }
    filterStr = `${filterStr},date__gte:${format(start, "yyyy-MM-dd")},date__lte:${format(end, "yyyy-MM-dd")}`;
    return filterStr;
  }, [advertisements, categoryId, isFilterAdvertisement, start, end]);

  const { data: dayhourData } = useCampaignStatsHourly(
    { dimensions: "hour,weekday", filters },
    { enabled: advertisements.length > 0 },
  );

  const { data: hourData } = useCampaignStatsHourly(
    { dimensions: "hour", filters },
    { enabled: advertisements.length > 0 },
  );

  const [selectedMetric, setSelectedMetric] = useState("ad_sales");
  const { t } = useTranslation(["advertising", "common", "metrics"]);
  const metricOptions = useMemo(
    () =>
      ["conversion_rate", "ctr", "impressions", "clicks", "ad_sales", "acos", "roas", "cpc", "ad_spend"].map((id) => {
        const metricSpec = getMetricSpec(id);
        return {
          value: metricSpec.id,
          label: t(metricNameSelector(metricSpec.id), { defaultValue: metricSpec.id }),
        };
      }),
    [t],
  );

  const [startDate, endDate] = useMemo((): [string | null, string | null] => {
    if (!hourData || hourData.length === 0) {
      return [null, null];
    }

    const startDates = hourData.map((d) => d.start_date).filter(Boolean);
    const endDates = hourData.map((d) => d.end_date).filter(Boolean);

    const minDate = startDates.length > 0 ? startDates.reduce((a, b) => (a < b ? a : b)) : null;
    const maxDate = endDates.length > 0 ? endDates.reduce((a, b) => (a > b ? a : b)) : null;

    return [minDate, maxDate];
  }, [hourData]);

  const chartMetrics = useMemo(() => {
    return metricOptions.map(({ value, label }) => {
      const spec = getMetricSpec(value);
      return {
        ...spec,
        name: label,
        formatter: getFormatter(marketPlace, spec.format, spec),
      };
    });
  }, [metricOptions, marketPlace]);

  const hourlyData = useMemo(() => {
    if (!hourData) {
      return [];
    }
    return hourData.sort((a, b) => (a.hour ?? 0) - (b.hour ?? 0));
  }, [hourData]);

  const initialSelection = useMemo(() => {
    return ["ad_sales", "ad_spend", "acos"];
  }, []);

  const [isEnabled, setIsEnabled] = useState(initialValue.day_parting_method === 1);
  const [bidModifiers, setBidModifiers] = useState(initialValue.day_parting_multipliers);

  const {
    selectedCells,
    selectedCount,
    isDragging,
    isPointerDown,
    toggleCell,
    clearSelection,
    startDrag,
    updateDrag,
    endDrag,
  } = useDaypartingSelection();
  const [bidValue, setBidValue] = useState(1);
  const getColor = useDaypartingColors({
    data: dayhourData ?? [],
    metricId: selectedMetric,
  });
  const getValue = useDaypartingValues({
    data: dayhourData ?? [],
    metricId: selectedMetric,
    marketPlace,
  });
  const metricName = t(metricNameSelector(selectedMetric), { defaultValue: selectedMetric });

  const handleEnabledChange = (enabled: boolean) => {
    setIsEnabled(enabled);
    if (!enabled) {
      clearSelection();
    }
    onChange?.({
      day_parting_method: enabled ? 1 : 0,
      day_parting_multipliers: bidModifiers,
    });
  };

  const handleApply = () => {
    const newModifiers = { ...bidModifiers };
    selectedCells.forEach((cellKey) => {
      const [hour, day] = parseCellKey(cellKey);
      newModifiers[createCellKey(hour, day)] = bidValue;
    });
    setBidModifiers(newModifiers);
    clearSelection();

    onChange?.({
      day_parting_method: isEnabled ? 1 : 0,
      day_parting_multipliers: newModifiers,
    });
  };

  const handleClear = () => {
    const keysToRemove = new Set(selectedCells);
    const newModifiers = Object.fromEntries(Object.entries(bidModifiers).filter(([key]) => !keysToRemove.has(key)));
    setBidModifiers(newModifiers);
    clearSelection();

    onChange?.({
      day_parting_method: isEnabled ? 1 : 0,
      day_parting_multipliers: newModifiers,
    });
  };

  const handleReset = () => {
    setIsEnabled(initialValue.day_parting_method === 1);
    setBidModifiers(initialValue.day_parting_multipliers);
    clearSelection();

    onChange?.({
      day_parting_method: initialValue.day_parting_method,
      day_parting_multipliers: initialValue.day_parting_multipliers,
    });
  };

  return (
    <VStack className={containerStyles} gap="medium" alignItems="stretch">
      {categoryId && (
        <HStack justifyContent="flex-end">
          <Switch isSelected={!isFilterAdvertisement} onChange={() => setIsFilterAdvertisement(!isFilterAdvertisement)}>
            {t(($) => $.advertising["Category"])}
          </Switch>
        </HStack>
      )}

      <HStack justifyContent="flex-end">
        <div className={periodInfoStyles}>
          {t(($) => $.advertising["Period: {startDate} - {endDate}"], {
            startDate: startDate ? format(new Date(startDate), "MMM d ''yy") : "",
            endDate: endDate ? format(new Date(endDate), "MMM d ''yy") : "",
          })}
          <br />
          {dateError && (
            <span className={errorTextStyles}>
              {t(($) => $.advertising["Hourly trends are limited to the last 30 days"])}
              <br />
            </span>
          )}
          <span>
            {t(($) => $.advertising["Timezone: {timezone}"], {
              timezone: (MARKETPLACE_TIMEZONE as Record<string, string>)[marketPlace.marketPlace] ?? "PST",
            })}
          </span>
        </div>
      </HStack>

      <HourChart
        data={hourlyData}
        metrics={chartMetrics}
        initialSelection={initialSelection}
        size={size}
        marketPlace={marketPlace}
      />

      <HStack justifyContent="space-between" alignItems="flex-start" width="100%">
        <DaypartingControls
          isEnabled={isEnabled}
          onEnabledChange={handleEnabledChange}
          selectedCount={selectedCount}
          bidValue={bidValue}
          onBidValueChange={setBidValue}
          onApply={handleApply}
          onClear={handleClear}
          onReset={handleReset}
          canUpdate={canUpdate}
        />

        <Select
          label={t(($) => $.advertising["Metric"])}
          items={metricOptions}
          value={selectedMetric}
          onChange={(key) => setSelectedMetric(key as string)}
          css={{ flex: "none", width: "250px" }}
        />
      </HStack>

      <DaypartingGrid
        bidModifiers={bidModifiers}
        selectedCells={selectedCells}
        isEnabled={isEnabled}
        getColor={getColor}
        getValue={getValue}
        metricName={metricName}
        onCellToggle={toggleCell}
        onDragStart={startDrag}
        onDragUpdate={updateDrag}
        onDragEnd={endDrag}
        isDragging={isDragging}
        isPointerDown={isPointerDown}
      />
    </VStack>
  );
}
